import { useEffect, useState } from "react";
import { portalApi, Page, usePortal } from "./PortalShell";
import { Copy, Share2, MessageCircle, Instagram, Send as TelegramIcon, Facebook, Link as LinkIcon, Check, Tag, Sparkles, RefreshCw, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const WA_URL = (text) => `https://wa.me/?text=${encodeURIComponent(text)}`;
const TG_URL = (text) => `https://t.me/share/url?url=${encodeURIComponent("https://fuelled.in")}&text=${encodeURIComponent(text)}`;
const FB_URL = (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
const TW_URL = (text) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;

const IG_TIP = "Instagram doesn't allow direct link-sharing on the web. We've copied the link — paste it into your story or bio.";

const VARIANTS = [
  { key: "stories", label: "Rewrite for Stories", short: "Stories", icon: Instagram, tone: "text-pink-200 border-pink-500/30 bg-pink-500/10 hover:bg-pink-500/20" },
  { key: "reels", label: "Rewrite for Reels", short: "Reels caption", icon: Sparkles, tone: "text-fuchsia-200 border-fuchsia-500/30 bg-fuchsia-500/10 hover:bg-fuchsia-500/20" },
  { key: "whatsapp", label: "Rewrite for WhatsApp", short: "WhatsApp broadcast", icon: MessageCircle, tone: "text-emerald-200 border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20" },
];

function pitchFor(code, url, coachName) {
  return `Hey — I've been using Fuelled and it's been solid.\n\nGet 16% off with my code: ${code}\n${url}\n\n— ${coachName || ""}`.trim();
}

export default function PortalSharePage() {
  const { profile } = usePortal();
  const [me, setMe] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    portalApi.get("/affiliate/me").then((r) => setMe(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Load failed"));
  }, []);

  if (err) return <Page title="Share"><div className="text-red-400">{err}</div></Page>;
  if (!me) return <Page title="Share"><div className="text-neutral-500 italic">Loading…</div></Page>;

  const codes = me.codes || [];

  return (
    <Page
      title="Share your code"
      subtitle="One code. One link. Every sale here pays you."
      testId="portal-share-page"
    >
      {codes.length === 0 ? (
        <div className="rounded-2xl bg-neutral-900 border border-neutral-800 p-6 text-center text-sm text-neutral-500">
          You don't have any active codes yet. Your ASM will drop one in as soon as you're onboarded.
        </div>
      ) : (
        <div className="space-y-4">
          {codes.map((c) => (
            <CodeCard key={c.code} c={c} coachName={profile.name} />
          ))}
        </div>
      )}
    </Page>
  );
}

function CodeCard({ c, coachName }) {
  const code = c.code;
  const url = c.affiliate_url || `https://fuelled.in/?bg_ref=${encodeURIComponent(code)}`;
  const defaultPitch = pitchFor(code, url, coachName);

  const [pitch, setPitch] = useState(defaultPitch);
  const [activeVariant, setActiveVariant] = useState(null); // "stories" | "reels" | "whatsapp" | null (=default)
  const [loadingVariant, setLoadingVariant] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  const isRewritten = activeVariant !== null;

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500);
    } catch {
      toast.error("Couldn't copy — long-press to copy manually");
    }
  };

  const shareInstagram = (text) => {
    navigator.clipboard?.writeText(text).finally(() => toast.message(IG_TIP));
  };

  const nativeShare = async (title, text, shareUrl) => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url: shareUrl });
      } catch {}
    } else {
      copy(`${text}\n${shareUrl}`, "native");
    }
  };

  const rewrite = async (variant) => {
    setLoadingVariant(variant);
    try {
      const r = await portalApi.post("/affiliate/ai/pitch/rewrite", { code, variant });
      const newPitch = (r.data?.pitch || "").trim();
      if (!newPitch) throw new Error("empty");
      setPitch(newPitch);
      setActiveVariant(variant);
      toast.success(`Rewritten for ${VARIANTS.find((v) => v.key === variant)?.short || variant}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI is taking a break — try again");
    } finally {
      setLoadingVariant(null);
    }
  };

  const restoreOriginal = () => {
    setPitch(defaultPitch);
    setActiveVariant(null);
  };

  const cKey = `code:${code}`;
  const uKey = `url:${code}`;

  return (
    <div data-testid={`share-code-card-${code}`} className="rounded-2xl bg-neutral-900 border border-neutral-800 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-neutral-800 bg-gradient-to-r from-orange-950/40 to-neutral-900 flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-orange-500/20 border border-orange-500/40 flex items-center justify-center">
          <Tag className="h-4 w-4 text-orange-300" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Your code</div>
          <div className="text-xl font-semibold tabular-nums">{code}</div>
        </div>
        <div className="ml-auto text-[11px] text-neutral-500">{c.usage_count || 0} uses</div>
      </div>

      {/* Code row */}
      <div className="px-5 py-4 border-b border-neutral-800/60 flex items-center gap-3">
        <div className="flex-1 min-w-0 rounded-xl bg-neutral-950 border border-neutral-800 px-3 py-2 font-mono text-sm truncate">{code}</div>
        <button
          onClick={() => copy(code, cKey)}
          data-testid={`copy-code-${code}`}
          className="rounded-xl bg-orange-500 hover:bg-orange-400 text-white h-10 w-10 flex items-center justify-center shrink-0"
          aria-label="Copy code"
        >
          {copiedKey === cKey ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>

      {/* Link row */}
      <div className="px-5 py-4 border-b border-neutral-800/60 flex items-center gap-3">
        <LinkIcon className="h-4 w-4 text-neutral-500 shrink-0" />
        <div className="flex-1 min-w-0 rounded-xl bg-neutral-950 border border-neutral-800 px-3 py-2 text-xs text-neutral-300 truncate">{url}</div>
        <button
          onClick={() => copy(url, uKey)}
          data-testid={`copy-url-${code}`}
          className="rounded-xl bg-neutral-800 hover:bg-neutral-700 text-white h-10 w-10 flex items-center justify-center shrink-0"
          aria-label="Copy link"
        >
          {copiedKey === uKey ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>

      {/* AI Rewrite row */}
      <div className="px-5 py-4 border-b border-neutral-800/60">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-3.5 w-3.5 text-orange-300" />
          <div className="text-[10px] uppercase tracking-widest text-neutral-400">AI-tuned pitches</div>
          {isRewritten && (
            <span data-testid={`pitch-variant-badge-${code}`} className="ml-auto text-[10px] uppercase tracking-widest text-orange-300 font-medium">
              {VARIANTS.find((v) => v.key === activeVariant)?.short}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {VARIANTS.map((v) => {
            const Icon = v.icon;
            const busy = loadingVariant === v.key;
            const active = activeVariant === v.key;
            return (
              <button
                key={v.key}
                disabled={loadingVariant !== null}
                onClick={() => rewrite(v.key)}
                data-testid={`rewrite-${v.key}-${code}`}
                className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-xs font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${v.tone} ${active ? "ring-1 ring-current" : ""}`}
              >
                {busy ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
                {busy ? "Writing…" : v.label}
              </button>
            );
          })}
          {isRewritten && (
            <button
              onClick={restoreOriginal}
              disabled={loadingVariant !== null}
              data-testid={`restore-original-${code}`}
              className="inline-flex items-center gap-2 rounded-full border border-neutral-700 bg-neutral-800 hover:bg-neutral-700 text-neutral-200 px-3.5 py-2 text-xs font-medium disabled:opacity-60"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Restore original
            </button>
          )}
        </div>
      </div>

      {/* Share row */}
      <div className="px-5 py-4">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Share it</div>
        <div className="flex flex-wrap gap-2">
          <a
            href={WA_URL(pitch)}
            target="_blank"
            rel="noreferrer"
            data-testid={`share-whatsapp-${code}`}
            className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-200 px-4 py-2 text-xs font-medium"
          >
            <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
          </a>
          <button
            onClick={() => shareInstagram(pitch)}
            data-testid={`share-instagram-${code}`}
            className="inline-flex items-center gap-2 rounded-full bg-pink-500/15 hover:bg-pink-500/25 border border-pink-500/30 text-pink-200 px-4 py-2 text-xs font-medium"
          >
            <Instagram className="h-3.5 w-3.5" /> Instagram
          </button>
          <a
            href={TG_URL(pitch)}
            target="_blank"
            rel="noreferrer"
            data-testid={`share-telegram-${code}`}
            className="inline-flex items-center gap-2 rounded-full bg-sky-500/15 hover:bg-sky-500/25 border border-sky-500/30 text-sky-200 px-4 py-2 text-xs font-medium"
          >
            <TelegramIcon className="h-3.5 w-3.5" /> Telegram
          </a>
          <a
            href={TW_URL(pitch)}
            target="_blank"
            rel="noreferrer"
            data-testid={`share-twitter-${code}`}
            className="inline-flex items-center gap-2 rounded-full bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 text-neutral-200 px-4 py-2 text-xs font-medium"
          >
            𝕏 Post
          </a>
          <a
            href={FB_URL(url)}
            target="_blank"
            rel="noreferrer"
            data-testid={`share-facebook-${code}`}
            className="inline-flex items-center gap-2 rounded-full bg-blue-500/15 hover:bg-blue-500/25 border border-blue-500/30 text-blue-200 px-4 py-2 text-xs font-medium"
          >
            <Facebook className="h-3.5 w-3.5" /> Facebook
          </a>
          <button
            onClick={() => nativeShare("My Fuelled code", pitch, url)}
            data-testid={`share-native-${code}`}
            className="inline-flex items-center gap-2 rounded-full bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 text-neutral-200 px-4 py-2 text-xs font-medium sm:hidden"
          >
            <Share2 className="h-3.5 w-3.5" /> More
          </button>
        </div>

        {/* Editable pitch preview */}
        <div className="mt-4 rounded-xl bg-neutral-950 border border-neutral-800 p-3">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">
              {isRewritten ? `${VARIANTS.find((v) => v.key === activeVariant)?.short} pitch` : "Message we send with WhatsApp / Telegram"}
            </div>
            {isRewritten && (
              <span className="text-[9px] uppercase tracking-widest text-neutral-500">· editable</span>
            )}
          </div>
          <textarea
            value={pitch}
            onChange={(e) => setPitch(e.target.value)}
            data-testid={`pitch-textarea-${code}`}
            rows={Math.min(10, Math.max(4, pitch.split("\n").length + 1))}
            className="w-full bg-transparent outline-none text-[12px] text-neutral-200 font-sans leading-relaxed resize-y"
          />
          <div className="mt-2 flex items-center justify-between gap-2">
            {isRewritten ? (
              <button
                onClick={() => rewrite(activeVariant)}
                disabled={loadingVariant !== null}
                data-testid={`regenerate-pitch-${code}`}
                className="text-[11px] text-neutral-400 hover:text-neutral-200 inline-flex items-center gap-1 disabled:opacity-60"
              >
                <RefreshCw className={`h-3 w-3 ${loadingVariant === activeVariant ? "animate-spin" : ""}`} /> Regenerate
              </button>
            ) : <span />}
            <button
              onClick={() => copy(pitch, `pitch:${code}`)}
              data-testid={`copy-pitch-${code}`}
              className="text-[11px] text-orange-300 hover:text-orange-200 inline-flex items-center gap-1"
            >
              {copiedKey === `pitch:${code}` ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              Copy message
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
